import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react"
import {
  Modal,
  Box,
  TextField,
  Card,
  CardMedia,
  Rating,
  Typography,
  Paper,
  IconButton,
  Stack,
  Dialog,
  Grid,
} from "@mui/material"
import { Share } from "@mui/icons-material"
import CloseIcon from "@mui/icons-material/Close"
import FullscreenIcon from "@mui/icons-material/Fullscreen"
import { Message as MessageIcon, Shop as ShopIcon } from "@mui/icons-material"
import Icn from "../../UI/SharieIcn/SharieIcn"
import Popup from "../../Popup/Popup"
import { useApiService } from "../../hooks/Api/useApiService"
import { useUser } from "../../hooks/Contexts/UserContext/UserContext"
import ChatModal from "../ChatModal/ChatModal"
import ModalCloseButton from "../ModalCloseButton"
import confetti from "canvas-confetti"
import T from "../../hooks/Language/T"
import useTranslationSharie from "../../hooks/Language/useTranslation"
import FunFactsModal from "../FunFactsModal/FunFactsModal"
import UserSection from "../../Marketplace/UserSection/UserSection"
import Sphere from "../../UI/Sphere"
import useError from "../../hooks/Error/useError"
import handslogo from "../../../assets/images/shariehandslogo.png"
import { useSocket } from "../../hooks/Utils/Socket/SocketManager"

const ViewSkillModal = forwardRef((props, ref) => {
  useImperativeHandle(ref, () => ({
    childFunction() {
      handleOpen()
    },
  }))
  const [level_color, setLevelColor] = useState("white")
  const [fun_facts, setFunFacts] = useState([])
  const [buying, setBuying] = useState(false)
  const [fullscreenImg, setFullscreenImg] = useState(false)
  const [open, setOpen] = useState(false)
  const [user, setUser] = useState(null)
  const handleOpen = () => setOpen(true)
  const handleClose = () => setOpen(false)
  const chatRef = useRef()
  const { postData, domain } = useApiService()
  const { context_user } = useUser()
  const t = useTranslationSharie()
  const funFactsRef = useRef()
  const post =
    props.skill?.wisdom === undefined &&
    props.skill?.duration !== "demand" &&
    props.skill?.image !== null
  const { Error: AlertError, error, success } = useError()
  const socket = useSocket()

  useEffect(() => {
    postData("users/getuser", { user_name: props.skill?.user_name }).then((data) => {
      setUser(data?.user[0])
    })
    props?.skill?.name && open && fetchFunFacts()
    props?.skill?.name && open && checkLevelColor()
  }, [props?.skill?.name, open])

  const checkLevelColor = () => {
    if (props.skill.level === "beginner") {
      setLevelColor("#4CAF50") // Green
    } else if (props.skill.level === "advanced") {
      setLevelColor("#FF9800") // Orange
    } else if (props.skill.level === "expert") {
      setLevelColor("#F44336") // Red
    } else {
      setLevelColor("white")
    }
  }
  const fetchFunFacts = async () => {
    try {
      postData("ai/funfacts", { skillName: props.skill?.name }).then((data) => {
        setFunFacts(data?.facts)
      })
    } catch (error) {
      error(error.message)
    }
  }

  const isVideo = () => {
    const mimeType = props.skill?.image?.split(";")[0] // Get the MIME type
    return mimeType?.startsWith("data:video/")
  }
  const openModal = (modalRef) => modalRef.current?.childFunction()
  const sendTransaction = async () => {
    try {
      const data = await postData("addtransaction", {
        skill_data: props.skill,
        from: localStorage.getItem("user"),
        is_group: props.skill?.max_participants ? true : false,
      })
      if (data.transactions === null || data.message !== "Successful") {
        error(data.message)
      } else {
        handleConfetti()
        success("Transaction Sent Successfully!")
        sendNotification("buy skill")
      }
    } catch (error) {
      error(error.message)
    }
    setBuying(false)
  }
  const sendNotification = (type) => {
    try {
      socket?.emit("send_notification", {
        toUserId: props.skill?.user_id,
        skill_name: props.skill?.name,
        from: localStorage.getItem("user"),
        to: props.skill?.user_name,
        type: type,
      })
      postData("addnotification", {
        skill_name: props.skill?.name,
        from: localStorage.getItem("user"),
        to: props.skill?.user_name,
        type: type,
      }).then((data) => {})
    } catch (error) {
      console.error("Error sending notification:", error)
    }
  }
  const handleConfetti = () => {
    confetti({
      particleCount: 100,
      spread: 70,
      origin: { y: 0.6 },
    })
  }
  const openFunFactsModal = (modalRef) => funFactsRef.current?.childFunction()

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: props.skill.name + " - " + props.skill.user_name || "Default Title",
          text: props.skill.description || "Default text to share",
          url: domain + "/shareskill/" + props.skill._id,
        })
        sendNotification("share skill")
      } catch (error) {
        error(error.message)
      }
    } else {
      error("Sharing is not supported")
    }
  }

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby='modal-title'
      aria-describedby='modal-description'
      style={{ pt: "3.4rem" }}
    >
      <T>
        <FunFactsModal ref={funFactsRef} fun_facts={fun_facts} skill_name={props.skill?.name} />

        <ModalCloseButton handleClose={handleClose} />
        <Paper
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: "90%",
            maxWidth: 500,
            p: 3,
            borderRadius: 2,
            boxShadow: 24,
            borderTop: "3px solid " + level_color,
          }}
        >
          <ChatModal
            ref={chatRef}
            chat_user={localStorage.getItem("user")}
            to={props.skill?.user_name}
            skill={props.skill}
          />
          <IconButton sx={{ position: "fixed", top: 8, right: 8 }} onClick={handleClose}>
            <CloseIcon />
          </IconButton>
          <IconButton
            sx={{
              position: "fixed",
              top: 8,
              right: 8,
              color: "#1976d2",
            }}
          >
            <Sphere image={handslogo} onClick={openFunFactsModal} />
          </IconButton>

          <Typography variant='h6' component='h2' textAlign='center' mb={2}>
            {props.skill?.name || props.skill?.wisdom?.question}
          </Typography>
          <UserSection user={user} />
          {post && (
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={t("Skill Cost (in credits)")}
                  value={props.skill?.cost}
                  margin='dense'
                  disabled
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={t("Location")}
                  value={props.skill?.location}
                  margin='dense'
                  disabled
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={t("Start Date")}
                  value={props.skill?.start_date?.toLocaleString()}
                  margin='dense'
                  disabled
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={t("Duration")}
                  value={props.skill?.duration}
                  margin='dense'
                  disabled
                />
              </Grid>
            </Grid>
          )}

          <TextField
            fullWidth
            label={t("Description")}
            value={props.skill?.description || props.skill?.wisdom?.quote}
            margin='dense'
            multiline
            rows={3}
            disabled
          />
          {props.skill?.wisdom && (
            <TextField
              fullWidth
              label={t("Reflection")}
              value={props.skill?.reflection}
              margin='dense'
              multiline
              rows={3}
              disabled
            />
          )}
          {post && (
            <Card sx={{ display: "flex", justifyContent: "center", mt: 2, p: 1, boxShadow: 2 }}>
              <Box sx={{ position: "relative", width: "100%" }}>
                {isVideo() ? (
                  <video autoPlay muted loop controls width='100%'>
                    <source src={props.skill?.image} type='video/mp4' />
                    Your browser does not support the video tag.
                  </video>
                ) : (
                  <CardMedia
                    component='img'
                    image={props.skill?.image}
                    alt='Skill image'
                    sx={{ height: 200, borderRadius: 1 }}
                  />
                )}

                {/* Fullscreen IconButton */}
                <IconButton
                  sx={{
                    position: "absolute",
                    top: 8,
                    right: 8,
                    color: "text.primary",
                    backgroundColor: "rgba(0, 0, 0, 0.5)", // Optional for visibility
                    zIndex: 10,
                  }}
                  onClick={() => setFullscreenImg(true)}
                >
                  <FullscreenIcon />
                </IconButton>
              </Box>

              {/* Fullscreen Dialog */}
              <Dialog
                open={fullscreenImg}
                onClose={() => setFullscreenImg(false)}
                maxWidth='xl'
                fullScreen
                sx={{ backgroundColor: "black" }}
              >
                <IconButton
                  sx={{
                    position: "absolute",
                    top: 10,
                    right: 10,
                    color: "text.primary",
                    background: "rgba(0,0,0,0.5)",
                    zIndex: 10,
                  }}
                  onClick={() => setFullscreenImg(false)}
                >
                  <CloseIcon />
                </IconButton>

                <Box
                  sx={{
                    width: "100vw",
                    height: "100vh",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  {isVideo() ? (
                    <video autoPlay muted loop controls width='100%'>
                      <source src={props.skill?.image} type='video/mp4' />
                      Your browser does not support the video tag.
                    </video>
                  ) : (
                    <img
                      src={props.skill?.image}
                      alt='Fullscreen'
                      style={{
                        width: "100vw",
                        height: "100vh",
                        objectFit: "contain",
                      }}
                    />
                  )}
                </Box>
              </Dialog>
            </Card>
          )}
          {post && (
            <Stack
              direction='row'
              alignItems='center'
              spacing={1}
              sx={{
                mt: 2,
                justifyContent:
                  props.skill?.user_name !== localStorage.getItem("user")
                    ? "space-between"
                    : "center",
              }}
            >
              {/* <Typography variant='body1'>Rating:</Typography> */}

              {props.skill?.user_name !== localStorage.getItem("user") && (
                <Icn
                  tooltip={"Share"}
                  color={"purple"}
                  onClick={handleShare}
                  label='share'
                  icon={<Share />}
                />
              )}

              <Rating name='skill-rating' value={props.skill?.rating || 0} precision={1} readOnly />

              {props.skill?.user_name !== localStorage.getItem("user") && (
                <>
                  <Icn
                    tooltip={
                      props.skill?.cost > context_user?.credit ? "Not Enough Credit!" : "Buy Skill"
                    }
                    color='#1976d2'
                    onClick={() => setBuying(true)}
                    label='purchase'
                    className={"shop_icon"}
                    icon={<ShopIcon />}
                  />
                  {buying && (
                    <Popup
                      open={buying}
                      handleClose={() => setBuying(false)}
                      handleConfirm={sendTransaction}
                    />
                  )}
                </>
              )}
            </Stack>
          )}
          <AlertError />
        </Paper>
      </T>
    </Modal>
  )
})

export default ViewSkillModal
